import { useCallback, useEffect, useRef, useState } from "react";
import type { RadioMessage, SimState } from "../domain/model";
import {
  readAudioPreferences,
  RadioAudio,
  defaultAudioPreferences,
  silentRuntime,
  type AudioPreferences,
} from "./radioAudio";
import { RadioInbox } from "./radioQueue";
import { request } from "../api";
import { germanVoices } from "./radioVoices";

export function useRadioAudio(
  state: SimState | null,
  connected: boolean,
  playerId: string,
) {
  const key = `leitwerk-audio-v1-${playerId}`;
  const [preferences, setPreferences] = useState<AudioPreferences>(() => {
    try {
      return readAudioPreferences(
        JSON.parse(localStorage.getItem(key) ?? "{}"),
      );
    } catch {
      return { ...defaultAudioPreferences };
    }
  });
  const [runtime, setRuntime] = useState({ ...silentRuntime });
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const engine = useRef<RadioAudio | null>(null);
  const inbox = useRef(new RadioInbox());
  const directElement = useRef<HTMLAudioElement | null>(null);
  const directRef = useCallback((element: HTMLAudioElement | null) => {
    directElement.current = element;
    engine.current?.attachDirect(element);
  }, []);
  useEffect(() => {
    const audio = new RadioAudio(preferences, setRuntime);
    engine.current = audio;
    audio.attachDirect(directElement.current);
    let disposed = false;
    void request<{ available: boolean }>("speech")
      .then((status) => {
        if (!disposed) audio.setNeuralAvailable(status.available);
      })
      .catch(() => {});
    const updateVoices = () =>
      setVoices(
        "speechSynthesis" in window
          ? germanVoices(window.speechSynthesis.getVoices())
          : [],
      );
    updateVoices();
    window.speechSynthesis?.addEventListener("voiceschanged", updateVoices);
    return () => {
      disposed = true;
      audio.dispose();
      engine.current = null;
      window.speechSynthesis?.removeEventListener(
        "voiceschanged",
        updateVoices,
      );
      inbox.current.disconnect();
    };
  }, [playerId]);
  useEffect(() => {
    engine.current?.setPreferences(preferences);
    try {
      localStorage.setItem(key, JSON.stringify(preferences));
    } catch {
      /* Audio remains usable when browser storage is disabled. */
    }
  }, [key, preferences]);
  useEffect(() => {
    if (!connected) {
      inbox.current.disconnect();
      engine.current?.stop();
      return;
    }
    if (state) engine.current?.receive(inbox.current.take(state.radioMessages));
  }, [state, connected]);
  const setCallerSpeaking = useCallback((speaking: boolean) => {
    engine.current?.setCallerSpeaking(speaking);
  }, []);
  async function activate() {
    const statusCheck = request<{ available: boolean }>("speech")
      .then((status) => engine.current?.setNeuralAvailable(status.available))
      .catch(() => {});
    const next = { ...preferences, enabled: true };
    setPreferences(next);
    engine.current?.setPreferences(next);
    await engine.current?.activate();
    // Only explicit activation waits for model availability; dispatch commands stay responsive.
    await Promise.race([
      statusCheck,
      new Promise((resolve) => setTimeout(resolve, 1600)),
    ]);
  }
  async function replay(message: RadioMessage) {
    await activate();
    engine.current?.replay(message);
  }
  async function test(playback?: AudioPreferences["playback"]) {
    // An explicit voice test must work even after speech was disabled or volume set to zero.
    const next = {
      ...preferences,
      enabled: true,
      speech: true,
      speechProvider: "auto" as const,
      playback: playback ?? preferences.playback,
      volume: preferences.volume || 0.6,
    };
    setPreferences(next);
    engine.current?.setPreferences(next);
    const ready = engine.current?.activate();
    await Promise.all([
      ready,
      request<{ available: boolean }>("speech")
        .then((status) => engine.current?.setNeuralAvailable(status.available))
        .catch(() => {}),
    ]);
    engine.current?.preview();
  }
  return {
    preferences,
    runtime,
    voices,
    directRef,
    setCallerSpeaking,
    activate,
    prepare: () => engine.current?.prepare(),
    replay,
    test,
    change: (patch: Partial<AudioPreferences>) =>
      setPreferences((p) => ({ ...p, ...patch })),
    stop: () => engine.current?.stop(),
  };
}
export type RadioController = ReturnType<typeof useRadioAudio>;
